/**
 * The assistant's typed edge on the shell intent bus.
 *
 * Kept in its own module, free of any heavy import, because it is reached from
 * `components/Panel` — which nearly every page renders. Pulling the conversation
 * UI in from here would drag the whole assistant into the main bundle and undo
 * the lazy split the widget registration relies on.
 *
 * Sending an intent *fills the composer* rather than firing the question. The
 * assistant runs against a metered daily request budget the user can see, so a
 * button that silently spends one on a question they have not read yet is the
 * wrong default — they get the phrasing handed to them, and decide.
 */
import { useMemo } from "react";

import { useIntentSender } from "@/shell/intents";

/** Intent id — matches the assistant's feature-flag key and shell widget id. */
export const ASSISTANT_INTENT = "chat";

export interface AskIntent {
  /** Question to place in the composer, ready to send. */
  question: string;
}

/** Runtime guard: the bus carries `unknown`, and the sender may be older code. */
export function isAskIntent(payload: unknown): payload is AskIntent {
  return (
    typeof payload === "object" &&
    payload !== null &&
    typeof (payload as { question?: unknown }).question === "string" &&
    (payload as AskIntent).question.trim().length > 0
  );
}

export interface AskAssistant {
  /** False when no assistant is mounted — render no entry point at all. */
  available: boolean;
  ask: (question: string) => void;
}

/** Open the assistant with `question` waiting in the composer. */
export function useAskAssistant(): AskAssistant {
  const sender = useIntentSender(ASSISTANT_INTENT);
  return useMemo(
    () => ({
      available: sender.available,
      ask: (question: string) => sender.send({ question } satisfies AskIntent),
    }),
    [sender],
  );
}
